import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Receipt, Link as LinkIcon, LinkBreak, MagnifyingGlass, ArrowSquareOut, CircleNotch, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

const fmt$ = (n) =>
  n == null || isNaN(n)
    ? "$0.00"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
};

const STATUS_COLORS = {
  current: "bg-[#2E7D32]/10 text-[#2E7D32]",
  past_due: "bg-[#F57C00]/15 text-[#F57C00]",
  overdue: "bg-[#E53935]/15 text-[#E53935]",
  collections: "bg-[#B71C1C]/15 text-[#B71C1C]",
};

/**
 * Billing panel embedded inside the Edit Customer modal.
 * Shows current Zoho link, balance, recent invoices, last payment.
 * Allows manual link/unlink + smart-match suggestions.
 */
export default function CustomerBillingPanel({ customerId, onLinkChange }) {
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [zohoStatus, setZohoStatus] = useState({ configured: false, connected: false });

  async function load() {
    setLoading(true);
    try {
      const [statusR, billingR] = await Promise.all([
        api.get("/integrations/zoho/status").catch(() => ({ data: { configured: false, connected: false } })),
        api.get(`/customers/${customerId}/billing`).catch(() => ({ data: null })),
      ]);
      setZohoStatus(statusR.data || { configured: false, connected: false });
      setBilling(billingR.data);
      if (!billingR.data?.linked && statusR.data?.configured) {
        try {
          const { data } = await api.get(`/customers/${customerId}/zoho-suggestions`);
          setSuggestions(data.suggestions || []);
        } catch { setSuggestions([]); }
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (customerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function searchContacts(q) {
    setSearchQ(q);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/integrations/zoho/search-contacts", { params: { q } });
      setSearchResults(data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function link(zohoContactId) {
    try {
      await api.post(`/customers/${customerId}/zoho-link`, { zoho_contact_id: zohoContactId });
      toast.success("Customer linked to Zoho Books");
      onLinkChange?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link failed");
    }
  }

  async function unlink() {
    if (!window.confirm("Unlink this customer from Zoho Books? Billing data will disappear until you link again.")) return;
    try {
      await api.delete(`/customers/${customerId}/zoho-link`);
      toast.success("Customer unlinked");
      setSearchQ("");
      setSearchResults([]);
      onLinkChange?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unlink failed");
    }
  }

  async function refresh() {
    try {
      const { data } = await api.post(`/customers/${customerId}/zoho-refresh`);
      const s = data?.stats || {};
      toast.success(`Refreshed · ${s.invoices ?? 0} invoices · ${s.payments ?? 0} payments${s.stale_invoices_removed ? ` · ${s.stale_invoices_removed} stale removed` : ""}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    }
  }

  if (loading) {
    return (
      <div className="bg-[#FAFAFA] border border-[#012340]/10 rounded-sm p-4 flex items-center gap-2 text-sm text-[#5C738A]" data-testid="customer-billing-panel">
        <CircleNotch className="animate-spin" /> Loading billing…
      </div>
    );
  }

  if (!zohoStatus.configured) {
    return (
      <div className="bg-[#FAFAFA] border border-dashed border-[#012340]/15 rounded-sm p-4 text-sm text-[#5C738A]" data-testid="customer-billing-panel">
        <div className="font-bold text-[#012340] mb-1">Billing — Zoho Books Not Configured</div>
        Configure Zoho Books in <a href="/admin/integrations" className="font-bold text-[#012340] underline hover:text-[#CFAB7A]">Settings → Integrations</a> to pull invoices and balances for this customer.
      </div>
    );
  }

  const linked = billing?.linked;
  const notConnectedNotice = !zohoStatus.connected ? (
    <div className="bg-[#F57C00]/10 border-l-4 border-[#F57C00] p-2.5 text-[11px] text-[#012340] rounded-sm" data-testid="customer-billing-not-connected-notice">
      Zoho Books is configured but not yet connected. Live invoice data will appear after an admin connects in <a href="/admin/integrations" className="font-bold underline hover:text-[#CFAB7A]">Settings → Integrations</a>.
    </div>
  ) : null;

  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm" data-testid="customer-billing-panel">
      <div className="px-4 py-3 border-b border-[#012340]/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt size={16} weight="bold" className="text-[#012340]" />
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#012340]">Billing (Zoho Books)</div>
        </div>
        {linked && (
          <div className="flex items-center gap-3">
            <button onClick={refresh} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#012340] hover:text-[#CFAB7A]" data-testid="customer-billing-refresh">
              <ArrowsClockwise size={12} weight="bold" /> Refresh from Zoho
            </button>
            <button onClick={unlink} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E53935] hover:underline" data-testid="customer-billing-unlink">
              <LinkBreak size={12} weight="bold" /> Unlink
            </button>
          </div>
        )}
      </div>

      {!linked && (
        <div className="p-4 space-y-4" data-testid="customer-billing-unlinked">
          {notConnectedNotice}
          {suggestions.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-2">Smart Match · Suggestions</div>
              <div className="space-y-1.5">
                {suggestions.map((s) => (
                  <div key={s.contact_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-[#012340]/10 hover:bg-[#FAFAFA]" data-testid={`smart-match-${s.contact_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#012340] truncate">{s.name}</div>
                      <div className="text-xs text-[#5C738A] truncate">{s.email || s.phone || s.company || "—"}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] bg-[#CFAB7A]/25 text-[#012340] px-2 py-0.5 rounded-sm">{Math.round(s.score * 100)}%</span>
                    <button onClick={() => link(s.contact_id)} className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm bg-[#012340] text-white hover:bg-[#012340]/90" data-testid={`smart-match-link-${s.contact_id}`}>
                      Approve
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-2">Manual Match · Search Zoho Contacts</div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C738A]" />
              <input
                value={searchQ}
                onChange={(e) => searchContacts(e.target.value)}
                placeholder="Search Zoho contact name, email, phone…"
                className="w-full pl-8 pr-3 py-2 rounded-sm border border-[#012340]/15 text-sm"
                data-testid="customer-billing-search"
              />
            </div>
            {searching && <div className="text-xs text-[#5C738A] mt-2 flex items-center gap-1"><CircleNotch className="animate-spin" /> Searching…</div>}
            {!searching && searchQ && searchResults.length === 0 && (
              <div className="text-xs text-[#5C738A] mt-2">No Zoho contacts match.</div>
            )}
            {searchResults.length > 0 && (
              <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto">
                {searchResults.map((r) => (
                  <div key={r.contact_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-[#012340]/10 hover:bg-[#FAFAFA]" data-testid={`zoho-search-${r.contact_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#012340] truncate">{r.name || r.company || "—"}</div>
                      <div className="text-xs text-[#5C738A] truncate">{r.email || r.phone || "—"} · {fmt$(r.outstanding)} outstanding</div>
                    </div>
                    <button onClick={() => link(r.contact_id)} className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm bg-[#CFAB7A] text-[#012340] hover:brightness-105" data-testid={`zoho-link-${r.contact_id}`}>
                      <LinkIcon size={10} weight="bold" className="inline mr-1" /> Link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {linked && billing && (
        <div className="p-4 space-y-4" data-testid="customer-billing-linked">
          {notConnectedNotice}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <BillStat label="Balance" value={fmt$(billing.balance)} mono />
            <BillStat label="Status" value={
              <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${STATUS_COLORS[billing.billing_status] || "bg-[#012340]/8 text-[#5C738A]"}`}>
                {(billing.billing_status || "current").replace("_", " ")}
              </span>
            } />
            <BillStat label="Billing Email" value={billing.billing_email || "—"} small />
            <BillStat label="Billing Phone" value={billing.billing_phone || "—"} small />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Recent Invoices ({billing.invoices.length})</div>
              <span className="text-[10px] text-[#5C738A]">{billing.zoho_contact_name || ""}</span>
            </div>
            {billing.invoices.length === 0 ? (
              <div className="text-xs text-[#5C738A] py-3">No invoices on file.</div>
            ) : (
              <div className="border border-[#012340]/10 rounded-sm overflow-hidden">
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAFAFA] sticky top-0">
                      <tr className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">
                        <th className="text-left px-3 py-2">Invoice</th>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Due</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-right px-3 py-2">Total</th>
                        <th className="text-right px-3 py-2">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#012340]/5">
                      {billing.invoices.slice(0, 25).map((inv) => (
                        <tr key={inv.invoice_id} data-testid={`customer-invoice-${inv.invoice_id}`}>
                          <td className="px-3 py-1.5 font-mono text-[#012340] font-semibold">{inv.invoice_number || inv.invoice_id}</td>
                          <td className="px-3 py-1.5 text-xs text-[#5C738A]">{fmtDate(inv.date)}</td>
                          <td className="px-3 py-1.5 text-xs text-[#5C738A]">{fmtDate(inv.due_date)}</td>
                          <td className="px-3 py-1.5 text-xs uppercase tracking-[0.12em] text-[#012340]">{inv.status || "—"}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmt$(inv.total)}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmt$(inv.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-[#5C738A]">
            <div>
              <span className="font-bold text-[#012340]">Last Payment:</span>{" "}
              {billing.last_payment
                ? <>{fmt$(billing.last_payment.amount)} · {fmtDate(billing.last_payment.date)} · {billing.last_payment.mode || "—"}</>
                : "None on file"}
            </div>
            <a href="https://books.zoho.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#012340] hover:text-[#CFAB7A]" data-testid="customer-billing-open-zoho">
              Open in Zoho <ArrowSquareOut size={11} weight="bold" />
            </a>
          </div>

          <div className="bg-[#FAFAFA] border-l-4 border-[#CFAB7A] p-2 text-[11px] text-[#5C738A] rounded-sm">
            <CheckCircle size={12} weight="bold" className="inline mr-1 text-[#2E7D32]" />
            Read-only view. Billing status does NOT affect service routing — overdue customers stay on their routes.
          </div>
        </div>
      )}
    </div>
  );
}

function BillStat({ label, value, mono, small }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{label}</div>
      <div className={`mt-0.5 text-[#012340] ${mono ? "font-mono" : ""} ${small ? "text-xs" : "text-sm font-semibold"}`}>{value}</div>
    </div>
  );
}
